import * as XLSX from "xlsx";
import { translate } from "../dictionary/Translator";
import ui from "./dictionary/ui";
import moment from "moment";
import orderStatusMappings from "./dictionary/order_status";
import itemStatusMappings from "./dictionary/order_item_status";
import shipModeMappings from "./dictionary/ship_modes";
import gsapShipModeMappings from "./dictionary/gsap_ship_modes.json";
import { isEmpty } from "lodash";
import carrierCodeMappings from "./dictionary/carrier_codes";

//packages for orderPDF generation.
import pdfMake from "pdfmake/build/pdfmake";
import "pdfmake/build/vfs_fonts";

var language;

const generateOrderSummaryData = (order) => {
  const columnMapping = {
    id: translate({ word: ui["ORDER_NUMBER"], language: language }),
    OrderDate: translate({ word: ui["ORDER_DATE"], language: language }),
    "Order Status": translate({ word: ui["ORDER_STATUS"], language: language }),
    "Order Sub Total": translate({
      word: ui["ORDER_SUB_TOTAL"],
      language: language,
    }),
    "Estimated Shipping": translate({
      word: ui["ORDER_ESTIMATED_SHIPPING"],
      language: language,
    }),
    "Estimated Tax": translate({
      word: ui["ORDER_ESTIMATED_TAX"],
      language: language,
    }),
    "Estimated Order Total": translate({
      word: ui["ORDER_ESTIMATED_TOTAL"],
      language: language,
    }),
    "Payment term": translate({
      word: ui["ORDER_PAYMENT_TERM"],
      language: language,
    }),
    "Ship Complete": translate({
      word: ui["ORDER_SHIPCOMPLETE"],
      language: language,
    }),
    "PO Number": translate({ word: ui["PO_NUMBER"], language: language }),
    "Shipping Address": translate({
      word: ui["ORDER_SHIPPING_ADDRESS"],
      language: language,
    }),
    "Shipping Comment": translate({
      word: ui["ORDER_SHIPPING_COMMENT"],
      language: language,
    }),
  };

  const orderSummaryHeader = Object.values(columnMapping);

  let mainStatus = "";
  mainStatus = order.orderStatus
    ? order.orderStatus[0].rejectionStatus === "C"
      ? translate({
          word: ui["STATUS_CANCELLED"],
          language: language,
        })
      : translate({
          word: orderStatusMappings[
            (order.orderStatus[0].overAllStatus
              ? order.orderStatus[0].overAllStatus
              : " ") +
              (order.orderStatus[0].rejectionStatus
                ? order.orderStatus[0].rejectionStatus
                : " ")
          ],
          language,
        })
    : "";
  let subStatus = "";
  subStatus =
    order.billingBlockCode ||
    order.deliveryBlockCode ||
    (order.orderStatus && order.orderStatus[0].creditStatus === "B")
      ? translate({
          word: ui["STATUS_AWAITING_OOA_REVIEW"],
          language: language,
        })
      : "";

  const orderSummaryRows = [
    order.id,
    order.orderDate ? moment(order.orderDate).format("MM/DD/YYYY") : "",
    mainStatus + subStatus,
    order.netValue ? "$" + order.netValue.toFixed(2) : "$0.00",
    order.netFreight ? "$" + order.netFreight.toFixed(2) : "$0.00",
    order.netTax ? "$" + order.netTax.toFixed(2) : "$0.00",
    order.netValue + order.netTax
      ? "$" + (order.netValue + order.netTax).toFixed(2)
      : "$0.00",
    order.descriptions ? order.descriptions.paymentTermsDesc : "",
    order.shipComplete ? order.shipComplete : "No",
    order.po,
    order.orderShippingAddress &&
      order.orderShippingAddress.name +
        ", " +
        order.orderShippingAddress.addressLine1 +
        ", " +
        order.orderShippingAddress.city +
        ", " +
        order.orderShippingAddress.state +
        ", " +
        order.orderShippingAddress.postalCode,
    order.shipComments,
  ];

  return { orderSummaryHeader, orderSummaryRows };
};

const generateLineItemSummaryData = (order) => {
  const lineItemSummaryHeader = [
    translate({ word: ui["MODEL"], language: language }),
    translate({ word: ui["GRADE_INDICATOR"], language: language }),
    translate({ word: ui["MODEL_DESCRIPTION"], language: language }),
    translate({ word: ui["ITEM_UNIT_PRICE"], language: language }),
    translate({ word: ui["ITEM_A_STOCK_QUANTITY"], language: language }),
    translate({ word: ui["ITEM_B_STOCK_QUANTITY"], language: language }),
    translate({ word: ui["ITEM_SUB_TOTAL"], language: language }),
    translate({ word: ui["ITEM_TAX"], language: language }),
    translate({ word: ui["ITEM_FREIGHT"], language: language }),
    translate({ word: ui["ITEM_STATUS"], language: language }),
    translate({ word: ui["ITEM_SHIPPIED_QUANTITY"], language: language }),
    translate({ word: ui["ITEM_RDD"], language: language }),
    translate({ word: ui["ITEM_SHIPPING_MODE"], language: language }),
    translate({ word: ui["ITEM_SCHDULED_QUANTITY"], language: language }),
    translate({ word: ui["ITEM_MAD"], language: language }),
    translate({ word: ui["ITEM_ESD"], language: language }),
    translate({ word: ui["ITEM_EDD"], language: language }),
  ];

  const deliveryInformationHeader = [
    translate({ word: ui["MODEL"], language: language }),
    translate({ word: ui["GRADE_INDICATOR"], language: language }),
    translate({ word: ui["ITEM_DELIVERED_QUANTITY"], language: language }),
    translate({ word: ui["ITEM_SHIPPING_CARRIERS"], language: language }),
    translate({ word: ui["ITEM_TRACKING_NUMBER"], language: language }),
    translate({ word: ui["ITEM_BILL_OF_LADING_NUMBER"], language: language }),
    translate({ word: ui["ITEM_WAREHOUSE_SHIPPING_DATE"], language: language }),
  ];

  let source =
    order.id.startsWith("US") || order.id.startsWith("CA") ? "GS" : "S";

  let mainStatus = "";
  mainStatus = order.orderStatus
    ? order.orderStatus[0].rejectionStatus === "C"
      ? translate({
          word: ui["STATUS_CANCELLED"],
          language: language,
        })
      : translate({
          word: orderStatusMappings[
            (order.orderStatus[0].overAllStatus
              ? order.orderStatus[0].overAllStatus
              : " ") +
              (order.orderStatus[0].rejectionStatus
                ? order.orderStatus[0].rejectionStatus
                : " ")
          ],
          language,
        })
    : "";

  let deliveryRows = [];

  const lineItemSummaryRows = order.items
    .map((item) => {
      let modifiedData = {};
      let itemStatus = translate({
        word: itemStatusMappings[
          (item.status[0].processingStatus
            ? item.status[0].processingStatus
            : " ") +
            (item.status[0].rejectionStatus
              ? item.status[0].rejectionStatus
              : " ")
        ],
        language,
      });
      let itemSubStatus = item.delivery.find((del) => del.creditHold === "B")
        ? translate({
            word: ui["STATUS_ON_CREDIT_REVIEW"],
            language: language,
          })
        : item.delivery.find((del) => del.PGIDate === null)
        ? translate({
            word: ui["STATUS_AWAITING_SHIPPING"],
            language: language,
          })
        : "";
      let item_categories = ["ZSDD", "ZCTO"],
        item_plants = ["US04", "US10"];
      if (source === "S") {
        item_categories = ["ZTS", "ZTS5", "ZTS4"];
        item_plants = ["0444", "0809", "0777", "DS01"];
      }
      let plant_category_check,
        display_ETA,
        item_delivered = String(itemStatus).toUpperCase() === "COMPLETED",
        item_shipped = item.shippedQuantity > 0,
        with_cat = item_categories.includes(item.item_category),
        with_plant = item_plants.includes(item.item_plant),
        plant_shipped = with_plant && item_shipped,
        category_delivered = with_cat && item_delivered;
      if (String(mainStatus).toUpperCase() === "COMPLETED") {
        plant_category_check = false;
        display_ETA =
          (!with_plant && !with_cat) ||
          (category_delivered && isEmpty(item.invoice));
      } else {
        plant_category_check =
          (with_cat && !item_delivered) ||
          (with_plant && !item_shipped) ||
          (category_delivered && isEmpty(item.invoice)) ||
          (plant_shipped && !with_cat && isEmpty(item.delivery));
        display_ETA = (!with_plant && !with_cat) || plant_category_check;
      }
      let itemShipMode =
        item.businessOut &&
        item.businessOut[0] &&
        (item.businessOut[0].shipMode || "");
      let shipMode = translate({
        word:
          source === "S"
            ? shipModeMappings[itemShipMode]
            : gsapShipModeMappings[itemShipMode],
        language,
      });
      let isGPWBStock;
      if (source === "S") {
        isGPWBStock = item.outProductCode.endsWith(".B") ? true : false;
        isGPWBStock === true ? (item.grade = "B") : (item.grade = "A");
      }
      let isBStock = item.grade === "B" ? true : false;

      modifiedData[lineItemSummaryHeader[0]] =
        source === "S"
          ? isGPWBStock
            ? item.outProductCode.slice(0, item.outProductCode.length - 2)
            : item.outProductCode
          : item.outProductCode;
      modifiedData[lineItemSummaryHeader[1]] = item.grade;
      modifiedData[lineItemSummaryHeader[2]] = item.shortText;
      modifiedData[lineItemSummaryHeader[3]] =
        "$" +
        (
          item.price.unitPrice -
          item.price.freight / item.requestedQuantity
        ).toFixed(2);
      modifiedData[lineItemSummaryHeader[4]] = !isBStock
        ? item.requestedQuantity
        : "NA";
      modifiedData[lineItemSummaryHeader[5]] = isBStock
        ? item.requestedQuantity
        : "NA";
      modifiedData[lineItemSummaryHeader[6]] =
        "$" + item.price.netValue.toFixed(2);
      modifiedData[lineItemSummaryHeader[7]] = "$" + item.price.tax.toFixed(2);
      modifiedData[lineItemSummaryHeader[8]] =
        "$" + item.price.freight.toFixed(2);
      modifiedData[lineItemSummaryHeader[9]] = itemStatus + itemSubStatus;
      modifiedData[lineItemSummaryHeader[10]] = item.shippedQuantity;
      modifiedData[lineItemSummaryHeader[11]] = item.scheduledLines[0]
        ? item.scheduledLines[0].RDD
          ? moment(item.scheduledLines[0].RDD).format("MM/DD/YYYY")
          : ""
        : "";
      modifiedData[lineItemSummaryHeader[12]] = shipMode;

      item.delivery.forEach((del) => {
        let delData = {};
        let carrier = carrierCodeMappings.find(
          (carrierCode) => del.shippingCarrier === carrierCode.CODE
        );
        let PGIDate = del.PGIDate;
        let PLDGIDate = del.PLDGIDate;

        delData[deliveryInformationHeader[0]] = !isGPWBStock
          ? del.material
          : del.material.slice(0, del.material.length - 2);
        delData[deliveryInformationHeader[1]] = item.grade;
        delData[deliveryInformationHeader[2]] = del.deliveredQty;
        delData[deliveryInformationHeader[3]] = carrier ? carrier.NAME : "";
        delData[deliveryInformationHeader[4]] = carrier
          ? del.trackingNumber
          : "";
        delData[deliveryInformationHeader[5]] = del.billOfLadingNumber;
        delData[deliveryInformationHeader[6]] =
          PGIDate || PLDGIDate
            ? del.PGIDate !== "0000-00-00"
              ? moment(del.PGIDate).format("MM/DD/YYYY")
              : moment(del.PLDGIDate).format("MM/DD/YYYY")
            : "";
        deliveryRows.push(delData);
      });

      if (item.nonZeroConfQtySchLines.length > 0) {
        return item.nonZeroConfQtySchLines.map((schItem) => {
          let schLines = { ...modifiedData };
          if (display_ETA) {
            schLines[lineItemSummaryHeader[13]] = schItem.confirmedQuantity;
            schLines[lineItemSummaryHeader[14]] = plant_category_check
              ? "TBD"
              : schItem.materialAvailableDate
              ? moment(schItem.materialAvailableDate).format("MM/DD/YYYY")
              : "";
            schLines[lineItemSummaryHeader[15]] = plant_category_check
              ? "TBD"
              : schItem.shippingDate
              ? moment(schItem.shippingDate).format("MM/DD/YYYY")
              : "";
            schLines[lineItemSummaryHeader[16]] = plant_category_check
              ? "TBD"
              : schItem.estimatedDeliveryDate
              ? moment(schItem.estimatedDeliveryDate).format("MM/DD/YYYY")
              : "";
          } else {
            schLines[lineItemSummaryHeader[13]] = "TBD";
            schLines[lineItemSummaryHeader[14]] = "TBD";
            schLines[lineItemSummaryHeader[15]] = "TBD";
            schLines[lineItemSummaryHeader[16]] = "TBD";
          }
          return schLines;
        });
      } else {
        modifiedData[lineItemSummaryHeader[13]] = "NA";
        modifiedData[lineItemSummaryHeader[14]] = "NA";
        modifiedData[lineItemSummaryHeader[15]] = "NA";
        modifiedData[lineItemSummaryHeader[16]] = "NA";
      }

      return modifiedData;
    })
    .flat();

  return {
    lineItemSummaryHeader,
    lineItemSummaryRows,
    deliveryInformationHeader,
    deliveryRows,
  };
};

const formatOrderData = (
  order,
  ORDER_SUMMARY,
  ORDER_LINE_ITEM_SUMMARY,
  ITEM_DELIVERY_INFORMATION
) => {
  const orderData = {};
  orderData.orderId = order.id;
  // Order Summer Data
  const { orderSummaryHeader, orderSummaryRows } =
    generateOrderSummaryData(order);
  orderData.summary = {
    title: ORDER_SUMMARY,
    value: [orderSummaryHeader, orderSummaryRows],
  };

  const {
    lineItemSummaryHeader,
    lineItemSummaryRows,
    deliveryInformationHeader,
    deliveryRows,
  } = order.items && generateLineItemSummaryData(order);

  if (lineItemSummaryRows.length >= 1) {
    orderData.lineItems = { title: ORDER_LINE_ITEM_SUMMARY, value: [] };
    orderData.lineItems.value.push(lineItemSummaryHeader);

    let lineItemColumn = {};
    lineItemSummaryHeader.forEach((column) => {
      lineItemColumn[column] = column;
    });

    lineItemSummaryRows.forEach((row) => {
      const data = Object.keys(lineItemColumn).map((key) => row[key]);
      orderData.lineItems.value.push(data);
    });
  }
  if (deliveryRows.length >= 1) {
    orderData.deliveryInfo = { title: ITEM_DELIVERY_INFORMATION, value: [] };

    orderData.deliveryInfo.value.push(deliveryInformationHeader);

    let deliveryColumn = {};
    deliveryInformationHeader.forEach((column) => {
      deliveryColumn[column] = column;
    });
    deliveryRows.forEach((row) => {
      const data = Object.keys(deliveryColumn).map((key) => row[key]);
      orderData.deliveryInfo.value.push(data);
    });
  }
  return orderData;
};

export const generateExcelForObject = (orders, lang) => {
  language = lang;
  const [ORDER_SUMMARY, ORDER_LINE_ITEM_SUMMARY, ITEM_DELIVERY_INFORMATION] = [
    translate({ word: ui["ORDER_SUMMARY"], language: language }),
    translate({ word: ui["ORDER_LINE_ITEM_SUMMARY"], language: language }),
    translate({ word: ui["ITEM_DELIVERY_INFORMATION"], language: language }),
  ];

  const wb = XLSX.utils.book_new();
  const ws_data = [];
  const merges = []; // Variables to keep track of merges
  let rowIndex = 0; // Row index starting from 0

  const addBlankRowBetweenOrders = () => {
    ws_data.push([
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
    ]);
    merges.push({
      s: { r: rowIndex, c: 0 },
      e: { r: rowIndex, c: 26 },
    });
    rowIndex++;
  };

  orders.forEach((item, itemIdx) => {
    // formate the order data
    const order = formatOrderData(
      item.data,
      ORDER_SUMMARY,
      ORDER_LINE_ITEM_SUMMARY,
      ITEM_DELIVERY_INFORMATION
    );

    const sections = ["summary", "lineItems"];
    if (order.deliveryInfo) sections.push("deliveryInfo");

    let sectionRowCount = 0;
    const sectionRowLengths = [];

    // Pre-calculate how many rows each section contributes (including headers)
    sections.forEach((section) => {
      const rows = order[section].value.length;
      sectionRowLengths.push(rows);
      sectionRowCount += rows;
    });

    const blankRowsBetweenSections = sections.length - 1;
    const totalRowsForOrder = sectionRowCount + blankRowsBetweenSections;

    const orderStartRow = rowIndex;

    // Merge orderId across the entire block
    merges.push({
      s: { r: orderStartRow, c: 0 },
      e: { r: orderStartRow + totalRowsForOrder - 1, c: 0 },
    });

    // Fill orderId in first row
    ws_data[rowIndex] = ws_data[rowIndex] || [];
    ws_data[rowIndex][0] = order.orderId;

    let currentRow = rowIndex;

    sections.forEach((section, index) => {
      const secData = order[section];
      const secTitle = secData.title;
      const secValues = secData.value;
      const secRowCount = secValues.length;

      // Merge section title in column B for all its rows
      merges.push({
        s: { r: currentRow, c: 1 },
        e: { r: currentRow + secRowCount - 1, c: 1 },
      });

      // Set title
      ws_data[currentRow] = ws_data[currentRow] || [];
      ws_data[currentRow][1] = secTitle;

      // Fill data for this section
      for (let i = 0; i < secRowCount; i++) {
        const row = currentRow + i;
        ws_data[row] = ws_data[row] || [];
        const rowData = secValues[i];
        for (let c = 0; c < rowData.length; c++) {
          ws_data[row][2 + c] = rowData[c];
        }
      }

      currentRow += secRowCount;

      // Add blank row after section (except after last section)
      if (index < sections.length - 1) {
        ws_data[currentRow] = ["", "", ""];
        currentRow += 1;
      }
    });

    rowIndex = currentRow;

    // Add blank colored row between orders except after last order
    if (itemIdx < orders.length - 1) {
      addBlankRowBetweenOrders();
      addBlankRowBetweenOrders();
    }
  });

  // Convert ws_data to worksheet
  const ws = XLSX.utils.aoa_to_sheet(ws_data);

  // Add merges
  ws["!merges"] = merges;

  // Add worksheet to workbook
  XLSX.utils.book_append_sheet(wb, ws, "Orders");

  // Generate Excel file and trigger download
  XLSX.writeFile(wb, "orders.xlsx");
};

// =====================================================================================

// Register the fonts

const generateSummaryTemplate = (order, mainStatus, subStatus) => {
  return [
    {
      text: `${translate({
        word: ui["ORDER_SUMMARY"],
        language: language,
      })}:`,
      style: "header",
    },
    {
      canvas: [
        {
          type: "line",
          x1: 0,
          y1: 0,
          x2: 515,
          y2: 0,
          lineWidth: 1,
          lineColor: "#d3d3d3",
        },
      ],
      margin: [0, 5, 0, 10],
    },
    {
      columns: [
        // First top-level column (40%)
        {
          width: "40%",
          table: {
            widths: ["50%", "50%"],
            body: [
              [
                {
                  text: `${translate({
                    word: ui["ORDER_SUB_TOTAL"],
                    language: language,
                  })}`,
                  bold: true,
                },
                {
                  text: `${
                    order.netValue ? "$" + order.netValue.toFixed(2) : "$0.00"
                  }`,
                  alignment: "right",
                },
              ],
              [
                {
                  text: `${translate({
                    word: ui["ORDER_ESTIMATED_SHIPPING"],
                    language: language,
                  })}`,
                  bold: true,
                },
                {
                  text: `${
                    order.netFreight
                      ? "$" + order.netFreight.toFixed(2)
                      : "$0.00"
                  }`,
                  alignment: "right",
                },
              ],
              [
                {
                  text: `${translate({
                    word: ui["ORDER_ESTIMATED_TAX"],
                    language: language,
                  })}`,
                  bold: true,
                },
                {
                  text: `${
                    order.netTax ? "$" + order.netTax.toFixed(2) : "$0.00"
                  }`,
                  alignment: "right",
                },
              ],
              [
                {
                  text: `${translate({
                    word: ui["ORDER_ESTIMATED_TOTAL"],
                    language: language,
                  })}`,
                  bold: true,
                },
                {
                  text: `${
                    order.netValue + order.netTax
                      ? "$" + (order.netValue + order.netTax).toFixed(2)
                      : "$0.00"
                  }`,
                  alignment: "right",
                },
              ],
            ],
          },
          layout: "noBorders",
        },

        // Second top-level column (60%)
        {
          width: "60%",
          table: {
            widths: ["auto", "auto"],
            body: [
              [
                {
                  text: `${translate({
                    word: ui["ORDER_NUMBER"],
                    language: language,
                  })}:`,
                  bold: true,
                },
                { text: `${order.id}` },
              ],
              [
                {
                  text: `${translate({
                    word: ui["ORDER_DATE"],
                    language: language,
                  })}:`,
                  bold: true,
                },
                {
                  text: `${
                    order.orderDate
                      ? moment(order.orderDate).format("MM/DD/YYYY")
                      : ""
                  }`,
                },
              ],
              [
                {
                  text: `${translate({
                    word: ui["ORDER_STATUS"],
                    language: language,
                  })}:`,
                  bold: true,
                },
                { text: `${mainStatus + subStatus}` },
              ],
              [
                {
                  text: `${translate({
                    word: ui["ORDER_PAYMENT_TERM"],
                    language: language,
                  })}`,
                  bold: true,
                },
                {
                  text: `${
                    order.descriptions
                      ? order.descriptions.paymentTermsDesc
                      : ""
                  }`,
                },
              ],
              [
                {
                  text: `${translate({
                    word: ui["ORDER_SHIPCOMPLETE"],
                    language: language,
                  })}`,
                  bold: true,
                },
                { text: `${order.shipComplete ? order.shipComplete : "No"}` },
              ],
              [
                {
                  text: `${translate({
                    word: ui["PO_NUMBER"],
                    language: language,
                  })}:`,
                  bold: true,
                },
                { text: `${order.po}` },
              ],
              [
                {
                  text: `${translate({
                    word: ui["ORDER_SHIPPING_ADDRESS"],
                    language: language,
                  })}`,
                  bold: true,
                },
                {
                  text: `${
                    (order.orderShippingAddress
                      ? order.orderShippingAddress.name
                      : "") +
                    "\n" +
                    (order.orderShippingAddress
                      ? order.orderShippingAddress.addressLine1
                      : "") +
                    "\n" +
                    (order.orderShippingAddress
                      ? order.orderShippingAddress.city + " "
                      : "") +
                    (order.orderShippingAddress
                      ? order.orderShippingAddress.state + " "
                      : "") +
                    (order.orderShippingAddress
                      ? order.orderShippingAddress.postalCode
                      : "")
                  }`,
                },
              ],
              [
                {
                  text: `${translate({
                    word: ui["ORDER_SHIPPING_COMMENT"],
                    language: language,
                  })}`,
                  bold: true,
                },
                { text: `${order.shipComments ? order.shipComments : ""}` },
              ],
            ],
          },
          layout: "noBorders",
        },
      ],
      columnGap: 20,
    },
  ];
};

const generateLineItemTemplate = (order, mainStatus, source) => {
  if (!order.items) return null;
  const lineItems = order.items.map((item, index) => {
    let itemStatus = translate({
      word: itemStatusMappings[
        (item.status[0].processingStatus
          ? item.status[0].processingStatus
          : " ") +
          (item.status[0].rejectionStatus
            ? item.status[0].rejectionStatus
            : " ")
      ],
      language,
    });
    let itemSubStatus = item.delivery.find((del) => del.creditHold === "B")
      ? translate({
          word: ui["STATUS_ON_CREDIT_REVIEW"],
          language: language,
        })
      : item.delivery.find((del) => del.PGIDate === null)
      ? translate({
          word: ui["STATUS_AWAITING_SHIPPING"],
          language: language,
        })
      : "";

    // new Values to the plant and categories
    let item_categories = ["ZSDD", "ZCTO"],
      item_plants = ["US04", "US10"];
    if (source === "S") {
      item_categories = ["ZTS", "ZTS5", "ZTS4"];
      item_plants = ["0444", "0809", "0777", "DS01"];
    }
    let plant_category_check,
      display_ETA,
      item_delivered = String(itemStatus).toUpperCase() === "COMPLETED",
      item_shipped = item.shippedQuantity > 0,
      with_cat = item_categories.includes(item.item_category),
      with_plant = item_plants.includes(item.item_plant),
      plant_shipped = with_plant && item_shipped,
      category_delivered = with_cat && item_delivered;
    if (String(mainStatus).toUpperCase() === "COMPLETED") {
      plant_category_check = false;
      display_ETA =
        (!with_plant && !with_cat) ||
        (category_delivered && isEmpty(item.invoice));
    } else {
      plant_category_check =
        (with_cat && !item_delivered) ||
        (with_plant && !item_shipped) ||
        (category_delivered && isEmpty(item.invoice)) ||
        (plant_shipped && !with_cat && isEmpty(item.delivery));
      display_ETA = (!with_plant && !with_cat) || plant_category_check;
    }
    let itemShipMode =
      item.businessOut &&
      item.businessOut[0] &&
      (item.businessOut[0].shipMode || "");
    let shipMode = translate({
      word:
        source === "S"
          ? shipModeMappings[itemShipMode]
          : gsapShipModeMappings[itemShipMode],
      language,
    });
    let isGPWBStock;
    if (source === "S") {
      //If Product ends with .B then we remove the .B from model
      isGPWBStock = item.outProductCode.endsWith(".B") ? true : false;
      //if GPWstock is true for GPW vistex and non-vistex credits then add grade
      isGPWBStock === true ? (item.grade = "B") : (item.grade = "A");
    }
    //Irrespective of source will check wheter it has grade "A" / "B"  after adding grade field in GPW to display the quanity respectively
    let isBStock = item.grade === "B" ? true : false;

    let shortQtyIssue = item.isShortQtyIssue;
    let confirmationRequired = false;
    let substutionRequired = false;
    let message1 = "";
    let message2 = "";
    let message3 = "";
    if (isBStock && shortQtyIssue) {
      confirmationRequired = true;
    }
    if (item.outProductCode !== item.productCode) {
      substutionRequired = true;
    }
    if (substutionRequired) {
      message1 =
        translate({ word: ui["ITEM_SUBSTITUTION_MESSAGE"], language }) +
        " " +
        item.productCode +
        ".";
    }
    if (confirmationRequired) {
      message2 = translate({ word: ui["ITEM_CONFIRMATION_MESSAGE"], language });
    }
    if (substutionRequired && confirmationRequired) {
      message2 =
        translate({ word: ui["ITEM_SUBSTITUTION_MESSAGE"], language }) +
        item.productCode +
        ". " +
        translate({ word: ui["ITEM_CONFIRMATION_MESSAGE"], language });
    }

    let result = [
      `\n${translate({ word: ui["GRADE_INDICATOR"], language })}\t\t\t  : ${
        item.grade
      }`,
      `${translate({ word: ui["MODEL_DESCRIPTION"], language })}\t: ${
        item.shortText
      }\n\n`,
      {
        columns: [
          {
            table: {
              widths: ["50%", "50%"],
              body: [
                [
                  {
                    text: `${translate({ word: ui["ITEM_COST"], language })}`,
                    colSpan: 2,
                    bold: true,
                    alignment: "center",
                  },
                  {},
                ],
                [
                  {
                    text: `${translate({
                      word: ui["ITEM_UNIT_PRICE"],
                      language,
                    })}`,
                    bold: true,
                  },
                  {
                    text: `$${(
                      item.price.unitPrice -
                      item.price.freight / item.requestedQuantity
                    ).toFixed(2)}`,
                  },
                ],
                [
                  {
                    text: `${
                      !isBStock
                        ? translate({
                            word: ui["ITEM_A_STOCK_QUANTITY"],
                            language,
                          })
                        : translate({
                            word: ui["ITEM_B_STOCK_QUANTITY"],
                            language,
                          })
                    }`,
                    bold: true,
                  },
                  { text: `${item.requestedQuantity}` },
                ],
                [
                  {
                    text: `${translate({
                      word: ui["ITEM_SUB_TOTAL"],
                      language,
                    })}`,
                    bold: true,
                  },
                  { text: `$${item.price.netValue.toFixed(2)}` },
                ],
                [
                  {
                    text: `${translate({ word: ui["ITEM_TAX"], language })}`,
                    bold: true,
                  },
                  { text: `$${item.price.tax.toFixed(2)}` },
                ],
                [
                  {
                    text: `${translate({
                      word: ui["ITEM_FREIGHT"],
                      language,
                    })}`,
                    bold: true,
                  },
                  { text: `$${item.price.freight.toFixed(2)}` },
                ],
              ],
            },
          },
          {
            table: {
              widths: ["auto", "auto"],
              body: [
                [
                  {
                    text: `${translate({
                      word: ui["ITEM_SHIPPING"],
                      language,
                    })}`,
                    colSpan: 2,
                    bold: true,
                    alignment: "center",
                  },
                  {},
                ],
                [
                  {
                    text: `${translate({ word: ui["ITEM_STATUS"], language })}`,
                    bold: true,
                  },
                  { text: `${itemStatus + itemSubStatus}` },
                ],
                [
                  {
                    text: `${translate({
                      word: ui["ITEM_SHIPPIED_QUANTITY"],
                      language,
                    })}`,
                    bold: true,
                  },
                  { text: `${item.shippedQuantity}` },
                ],
                [
                  {
                    text: `${translate({ word: ui["ITEM_RDD"], language })}`,
                    bold: true,
                  },
                  {
                    text: `${
                      item.scheduledLines[0]
                        ? item.scheduledLines[0].RDD
                          ? moment(item.scheduledLines[0].RDD).format(
                              "MM/DD/YYYY"
                            )
                          : ""
                        : ""
                    }`,
                  },
                ],
                [
                  {
                    text: `${translate({
                      word: ui["ITEM_SHIPPING_MODE"],
                      language,
                    })}`,
                    bold: true,
                  },
                  { text: `${shipMode}` },
                ],
                [
                  {
                    text: `${translate({
                      word: ui["ITEM_SHIPPING_ADDRESS"],
                      language,
                    })}`,
                    bold: true,
                  },
                  {
                    text: `${
                      item.shippingAddress
                        ? `${item.shippingAddress.name}\n${
                            item.shippingAddress.addressLine1
                          }\n${
                            (item.shippingAddress.city,
                            item.shippingAddress.state,
                            item.shippingAddress.postalCode)
                          }`
                        : ""
                    }`,
                  },
                ],
              ],
            },
          },
        ],
        columnGap: 10,
      },
      "\n",
    ];

    if (item.nonZeroConfQtySchLines.length > 0) {
      let schedule = [];
      if (display_ETA) {
        schedule.push([
          {
            text: `${translate({
              word: ui["ITEM_SCHEDULE"],
              language,
            })}`,
            colSpan: 4,
            alignment: "center",
            bold: true,
            margin: [0, 5, 0, 5],
          },
          {},
          {},
          {},
        ]);
        schedule.push([
          {
            text: `${translate({
              word: ui["ITEM_SCHDULED_QUANTITY"],
              language,
            })}`,
            bold: true,
            alignment: "center",
          },
          {
            text: `${translate({ word: ui["ITEM_MAD"], language })}`,
            bold: true,
            alignment: "center",
          },
          {
            text: `${translate({ word: ui["ITEM_ESD"], language })}`,
            bold: true,
            alignment: "center",
          },
          {
            text: `${translate({ word: ui["ITEM_EDD"], language })}`,
            bold: true,
            alignment: "center",
          },
        ]);
        item.nonZeroConfQtySchLines.forEach((schItem, i) => {
          schedule.push([
            schItem.confirmedQuantity,
            plant_category_check
              ? "TBD"
              : schItem.materialAvailableDate
              ? moment(schItem.materialAvailableDate).format("MM/DD/YYYY")
              : "",
            plant_category_check
              ? "TBD"
              : schItem.shippingDate
              ? moment(schItem.shippingDate).format("MM/DD/YYYY")
              : "",
            plant_category_check
              ? "TBD"
              : schItem.estimatedDeliveryDate
              ? moment(schItem.estimatedDeliveryDate).format("MM/DD/YYYY")
              : "",
          ]);
        });
      } else {
        schedule.push([
          {
            text: `${translate({
              word: ui["ITEM_SCHEDULE"],
              language,
            })}`,
            colSpan: 2,
            alignment: "center",
            bold: true,
            margin: [0, 5, 0, 5],
          },
          {},
        ]);
        schedule.push([
          {
            text: `${translate({
              word: ui["ITEM_SCHDULED_QUANTITY"],
              language,
            })}`,
            bold: true,
            alignment: "center",
          },
          {
            text: `ETA`,
            bold: true,
            alignment: "center",
          },
        ]);

        item.nonZeroConfQtySchLines.forEach((schItem, i) => {
          schedule.push([
            schItem.confirmedQuantity,
            !isEmpty(item.invoice) || !isEmpty(item.delivery)
              ? translate({
                  word: ui["SEE_BELOW_DELIVERY_INFO"],
                  language,
                })
              : "TBD",
          ]);
        });
      }

      result.push({
        table: {
          body: schedule,
        },
      });
      result.push("\n\n");
    }

    return [
      {
        text: `${
          source === "S"
            ? isGPWBStock
              ? item.outProductCode.slice(0, item.outProductCode.length - 2)
              : item.outProductCode
            : item.outProductCode
        } \n ${
          confirmationRequired && substutionRequired
            ? message3
            : confirmationRequired
            ? message2
            : substutionRequired
            ? message1
            : ""
        }`,
        bold: true,
      },
      result,
    ];
  });

  let lineItemArr = [
    {
      text: `${translate({
        word: ui["ORDER_LINE_ITEM_SUMMARY"],
        language: language,
      })}:`,
      style: "header",
    },
    {
      canvas: [
        {
          type: "line",
          x1: 0,
          y1: 0,
          x2: 515,
          y2: 0,
          lineWidth: 1,
          lineColor: "#d3d3d3",
        },
      ],
      margin: [0, 5, 0, 10],
    },
  ];
  let obj = {
    table: {
      widths: ["15%", "85%"],
      body: [
        [
          {
            text: `${translate({
              word: ui["MODEL"],
              language: language,
            })}`,
            bold: true,
          },
          {
            text: "Details",
            bold: true,
          },
        ],
        ...lineItems,
      ],
    },
  };
  lineItemArr.push(obj);
  return lineItemArr;
};

const generateDeliveryTemplate = (order) => {
  if (!order.items) return null;
  let deliveryCount = 0;
  order.items.forEach((item) => {
    deliveryCount = deliveryCount + item.delivery.length;
  });
  let deliveryItems = [];
  if (deliveryCount > 0) {
    deliveryItems.push([
      {
        text: `${translate({ word: ui["MODEL"], language: language })}`,
        bold: true,
      },
      {
        text: `${translate({
          word: ui["GRADE_INDICATOR"],
          language: language,
        })}`,
        bold: true,
      },
      {
        text: `${translate({
          word: ui["ITEM_DELIVERED_QUANTITY"],
          language: language,
        })}`,
        bold: true,
      },
      {
        text: `${translate({
          word: ui["ITEM_SHIPPING_CARRIERS"],
          language: language,
        })}`,
        bold: true,
      },
      {
        text: `${translate({
          word: ui["ITEM_TRACKING_NUMBER"],
          language: language,
        })}`,
        bold: true,
      },
      {
        text: `${translate({
          word: ui["ITEM_BILL_OF_LADING_NUMBER"],
          language: language,
        })}`,
        bold: true,
      },
      {
        text: `${translate({
          word: ui["ITEM_WAREHOUSE_SHIPPING_DATE"],
          language: language,
        })}`,
        bold: true,
      },
    ]);

    order.items.forEach((item) => {
      item.delivery.forEach((del, i) => {
        let isGPWBStock = del.material.endsWith(".B") ? true : false;
        let carrier = carrierCodeMappings.find(
          (carrierCode) => del.shippingCarrier === carrierCode.CODE
        );
        deliveryItems.push([
          !isGPWBStock
            ? del.material
            : del.material.slice(0, del.material.length - 2),
          item.grade,
          del.deliveredQty,
          carrier ? carrier.NAME : "",
          del.trackingNumber,
          del.billOfLadingNumber,
          del.PGIDate !== "0000-00-00"
            ? moment(del.PGIDate).format("MM/DD/YYYY")
            : del.PLDGIDate
            ? moment(del.PLDGIDate).format("MM/DD/YYYY")
            : "",
        ]);
      });
    });
  } else {
    return null;
  }

  let deliveryItemArr = [
    {
      text: `${translate({
        word: ui["ITEM_DELIVERY_INFORMATION"],
        language: language,
      })}:`,
      style: "header",
    },
    {
      canvas: [
        {
          type: "line",
          x1: 0,
          y1: 0,
          x2: 515,
          y2: 0,
          lineWidth: 1,
          lineColor: "#d3d3d3",
        },
      ],
      margin: [0, 5, 0, 10],
    },
  ];

  let obj = {
    table: {
      body: deliveryItems,
    },
    margin: [0, 10, 0, 10],
  };
  deliveryItemArr.push(obj);
  return deliveryItemArr;
};
const generateInvoiceTemplate = (order) => {
  if (!order.items) return null;
  let invoiceCount = 0;
  order.items.forEach((item) => {
    item.invoice.forEach((inv) => {
      if (inv.docType === "M") {
        invoiceCount++;
      }
    });
  });
  let invoiceItems = [];
  if (invoiceCount > 0) {
    invoiceItems.push([
      { text: `${translate({ word: ui["MODEL"], language: language })}`, bold: true },
      { text: `${translate({ word: ui["GRADE_INDICATOR"], language: language })}`, bold: true },
      { text: `${translate({ word: ui["ITEM_INVOICE_NUMBER"], language: language })}`, bold: true },
      { text: `${translate({ word: ui["ITEM_INVOICE_DATE"], language: language })}`, bold: true },
      { text: `${translate({ word: ui["ITEM_INVOICE_QUANTITY"], language: language })}`, bold: true },
      { text: `${translate({ word: ui["ITEM_SUB_TOTAL"], language: language })}`, bold: true },
      { text: `${translate({ word: ui["ITEM_TAX"], language: language })}`, bold: true },
      { text: `${translate({ word: ui["ITEM_FREIGHT"], language: language })}`, bold: true },
      { text: `${translate({
        word: ui["ITEM_TOTAL"],
        language: language,
      })}`, bold: true },
    ]);

    order.items.forEach((item) => {
      item.invoice.forEach((inv, i) => {
        let isGPWBStock = inv.material.endsWith(".B") ? true : false;
        if (inv.docType === "M") {
          invoiceItems.push([
            !isGPWBStock
              ? inv.material
              : inv.material.slice(0, inv.material.length - 2),
            item.grade,
            inv.invoiceNumber,
            inv.creationDate
              ? moment(inv.creationDate).format("MM/DD/YYYY")
              : "",
            inv.invoicedQuantity,
            "$" + inv.lineNetValue.toFixed(2),
            "$" + inv.lineTax.toFixed(2),
            "$" + inv.lineFreight.toFixed(2),
            "$" + (inv.lineNetValue + inv.lineTax).toFixed(2),
          ]);
        }
      });
    });
  } else {
    return null;
  }

  let invoiceItemArr = [
    {
      text: `${translate({
        word: ui["ITEM_INVOICE_INFORMATION"],
        language: language,
      })}:`,
      style: "header",
    },
    {
      canvas: [
        {
          type: "line",
          x1: 0,
          y1: 0,
          x2: 515,
          y2: 0,
          lineWidth: 1,
          lineColor: "#d3d3d3",
        },
      ],
      margin: [0, 5, 0, 10],
    },
  ];

  let obj = {
    table: {
      body: invoiceItems,
    },
    margin: [0, 10, 0, 10],
  };
  invoiceItemArr.push(obj);
  return invoiceItemArr;
};

export const generateOrderPDF = (
  order,
  lang,
  mainStatus,
  subStatus,
  source
) => {
  language = lang;
  const summary = generateSummaryTemplate(order, mainStatus, subStatus);
  const lineItem = generateLineItemTemplate(order, mainStatus, source);
  const deliveryList = generateDeliveryTemplate(order);
  const invoiceList = generateInvoiceTemplate(order);
  const docDefinition = {
    content: [
      {
        text: `${translate({ word: ui["ORDER_DETAILS"], language: language })}`,
        style: { bold: true },
      },
      ...(summary||[]),
      "\n\n",
      ...(lineItem||[]),
      "\n\n",
      ...(deliveryList||[]),
      "\n\n",
      ...(invoiceList||[]),
    ],
    defaultStyle: {
      fontSize: 8,
    },
    styles: {
      header: {
        fontSize: 12,
        bold: true,
        margin: [0, 10, 0, 5],
      },
    },
  };

  pdfMake.createPdf(docDefinition).download(`${order.id}.pdf`);
};
